import { useEffect, useRef, RefObject } from 'react';

import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

type UseSynchronizedRefetchArgs = {
  syncGroup: string;
  intervalMs: number;
  enabled: boolean;
  onRefetch?: () => void;
};

type TimerEntry = {
  syncGroup: string;
  intervalMs: number;
  queryClient: QueryClient;
  refCount: number;
  timeoutId?: number;
  intervalId?: number;
  cancelled: boolean;
};

const timersByGroup = new Map<string, TimerEntry>();

const stopEntry = (entry: TimerEntry) => {
  entry.cancelled = true;

  if (entry.timeoutId !== undefined) {
    clearTimeout(entry.timeoutId);
    entry.timeoutId = undefined;
  }

  if (entry.intervalId !== undefined) {
    clearInterval(entry.intervalId);
    entry.intervalId = undefined;
  }
};

const tick = (entry: TimerEntry) => {
  return entry.queryClient.refetchQueries({
    type: 'active',
    predicate: (q) => q.meta?.syncGroup === entry.syncGroup,
  });
};

const startEntry = (
  entry: TimerEntry,
  onRefetchRef: RefObject<(() => void) | undefined>
) => {
  if (entry.timeoutId !== undefined || entry.intervalId !== undefined) {
    return;
  }

  entry.cancelled = false;

  const delayToBoundary = entry.intervalMs - (Date.now() % entry.intervalMs);

  entry.timeoutId = window.setTimeout(() => {
    if (entry.cancelled) {
      return;
    }
    onRefetchRef.current?.();
    void tick(entry);

    entry.intervalId = window.setInterval(() => {
      onRefetchRef.current?.();
      void tick(entry);
    }, entry.intervalMs);
  }, delayToBoundary);
};

const getOrCreateEntry = (
  syncGroup: string,
  intervalMs: number,
  queryClient: QueryClient
) => {
  const existing = timersByGroup.get(syncGroup);

  if (existing) {
    if (
      existing.intervalMs !== intervalMs ||
      existing.queryClient !== queryClient
    ) {
      stopEntry(existing);
      existing.intervalMs = intervalMs;
      existing.queryClient = queryClient;
    }

    return existing;
  }

  const created: TimerEntry = {
    syncGroup,
    intervalMs,
    queryClient,
    refCount: 0,
    cancelled: false,
  };

  timersByGroup.set(syncGroup, created);
  return created;
};

export const useSynchronizedRefetch = ({
  syncGroup,
  intervalMs,
  enabled,
  onRefetch,
}: UseSynchronizedRefetchArgs) => {
  const queryClient = useQueryClient();
  const onRefetchRef = useRef(onRefetch);
  // eslint-disable-next-line react-hooks/refs
  onRefetchRef.current = onRefetch;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const entry = getOrCreateEntry(syncGroup, intervalMs, queryClient);
    entry.refCount += 1;
    startEntry(entry, onRefetchRef);

    return () => {
      entry.refCount -= 1;

      if (entry.refCount <= 0) {
        stopEntry(entry);
        timersByGroup.delete(syncGroup);
      }
    };
  }, [enabled, queryClient, syncGroup, intervalMs]);
};
